/**
 * P5-17 · Review-media constraints shared by the form (pre-flight UX
 * rejection) and the router (authoritative re-enforcement). The mime *sets*
 * are fixed template-wide; store config only sizes/counts uploads and
 * toggles video (`reviews.media`).
 */
import { formatBytes } from "~/lib/media-validate";
import type { ReviewsMediaConfig } from "~/server/api/schemas/store-settings";

export const REVIEW_IMAGE_MIMES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

export const REVIEW_VIDEO_MIMES = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
] as const;

export const REVIEW_MEDIA_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
};

/** IMAGE/VIDEO for an allowed mime, null for anything else. */
export function reviewMediaKind(mime: string): "IMAGE" | "VIDEO" | null {
  if ((REVIEW_IMAGE_MIMES as readonly string[]).includes(mime)) return "IMAGE";
  if ((REVIEW_VIDEO_MIMES as readonly string[]).includes(mime)) return "VIDEO";
  return null;
}

export function allowedReviewMimes(config: Pick<ReviewsMediaConfig, "allowVideo">): string[] {
  return config.allowVideo
    ? [...REVIEW_IMAGE_MIMES, ...REVIEW_VIDEO_MIMES]
    : [...REVIEW_IMAGE_MIMES];
}

/**
 * Human-readable rejection reason, or null when acceptable. Mirrors
 * `media-validate.rejectionReason` semantics: a rejection is deterministic
 * (dismiss-only), distinct from a transient transfer failure.
 */
export function reviewMediaRejection(
  file: { type: string; size: number },
  config: ReviewsMediaConfig,
): string | null {
  const kind = reviewMediaKind(file.type);
  if (kind === null || (kind === "VIDEO" && !config.allowVideo)) {
    const label = config.allowVideo ? "JPEG, PNG, WebP, MP4 or WebM" : "JPEG, PNG or WebP";
    return `Unsupported type — use ${label}.`;
  }
  const cap = kind === "IMAGE" ? config.maxImageBytes : config.maxVideoBytes;
  if (file.size > cap) {
    return `Too large — ${formatBytes(file.size)} exceeds the ${formatBytes(cap)} limit.`;
  }
  return null;
}
